import * as OTPAuth from 'otpauth';

export type OtpSourceType = 'base32' | 'otpauth';

export interface ParsedTotpConfig {
  sourceType: OtpSourceType;
  issuer: string;
  label: string;
  algorithm: string;
  digits: number;
  period: number;
  secretBase32: string;
  totp: OTPAuth.TOTP;
}

export interface LocalTwoFaAccount {
  localId: string;
  secretBase32: string;
  issuer: string;
  label: string;
  algorithm: string;
  digits: number;
  period: number;
  displayName: string;
  sourceRaw: string;
  note?: string;
  isMinimal?: boolean;
}

export interface TotpShareConfig {
  secretBase32: string;
  issuer?: string;
  label?: string;
  algorithm?: string;
  digits?: number;
  period?: number;
  fallbackLabel?: string;
}

export type AccountExportFormat = 'links' | 'secrets' | 'rows';

export interface AccountExportItem {
  secretBase32: string;
  sourceRaw?: string;
  displayName?: string;
}

export interface BulkLinkTransformResult {
  text: string;
  replaced: number;
  unchanged: number;
}

export const PERMANENT_LINK_ORIGIN = 'https://2fa.3a-fb.info';

export function buildPermanentLink(secretBase32: string): string {
  const normalized = normalizeBase32Secret(secretBase32);
  if (!normalized) {
    throw new Error('Нельзя создать постоянную ссылку без секрета');
  }
  return `${PERMANENT_LINK_ORIGIN}/${encodeURIComponent(normalized)}`;
}

export function buildOtpAuthUri(config: TotpShareConfig): string {
  const secretBase32 = normalizeBase32Secret(config.secretBase32);
  const issuer = (config.issuer || '').trim();
  const label = (config.label || config.fallbackLabel || issuer || '2FA').trim();
  const totp = new OTPAuth.TOTP({
    issuer,
    label,
    secret: OTPAuth.Secret.fromBase32(secretBase32),
    algorithm: normalizeAlgorithm(config.algorithm),
    digits: normalizePositiveNumber(config.digits, 6),
    period: normalizePositiveNumber(config.period, 30),
  });

  return OTPAuth.URI.stringify(totp);
}

export function buildAccountExportText(
  items: AccountExportItem[],
  format: AccountExportFormat,
): string {
  return items.map((item) => {
    const secretBase32 = normalizeBase32Secret(item.secretBase32);
    if (format === 'links') return buildPermanentLink(secretBase32);
    if (format === 'secrets') return secretBase32;

    const sourceRaw = (item.sourceRaw || '').trim();
    if (sourceRaw) return sourceRaw;
    return [(item.displayName || '').trim(), secretBase32].filter(Boolean).join(' ');
  }).join('\n');
}

export function normalizeBase32Secret(value: string): string {
  return value.replace(/[\s-]+/g, '').toUpperCase();
}

export function normalizePositiveNumber(value: unknown, fallback: number): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return fallback;
  }
  return Math.trunc(numeric);
}

export function normalizeAlgorithm(value: unknown): string {
  if (typeof value !== 'string' || value.trim() === '') {
    return 'SHA1';
  }
  return value.toUpperCase();
}

export function generateLocalId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function buildLocalDisplayName(issuer: string, label: string, fallbackNumber: number): string {
  const parts = [issuer, label].map((part) => part.trim()).filter((part) => part.length > 0);
  if (parts.length > 0) {
    return parts.join(' / ');
  }
  return `Аккаунт ${fallbackNumber}`;
}

export function maskSecret(secret: string): string {
  if (secret.length <= 8) return secret;
  return `${secret.slice(0, 4)}••••${secret.slice(-4)}`;
}

export function parseTotpSource(source: string): ParsedTotpConfig {
  const trimmed = source.trim();

  if (!trimmed) {
    throw new Error('Введите код 2FA/секрет');
  }

  if (/^otpauth:\/\//i.test(trimmed)) {
    let parsedOtp: OTPAuth.HOTP | OTPAuth.TOTP;

    try {
      parsedOtp = OTPAuth.URI.parse(trimmed);
    } catch {
      throw new Error('Некорректный otpauth:// URI');
    }

    if (!(parsedOtp instanceof OTPAuth.TOTP)) {
      throw new Error('Поддерживается только otpauth://totp/...');
    }

    const issuer = typeof parsedOtp.issuer === 'string' ? parsedOtp.issuer : '';
    const label = typeof parsedOtp.label === 'string' ? parsedOtp.label : '';
    const digits = normalizePositiveNumber(parsedOtp.digits, 6);
    const period = normalizePositiveNumber(parsedOtp.period, 30);
    const algorithm = normalizeAlgorithm(parsedOtp.algorithm);
    const secretBase32 = parsedOtp.secret.base32;
    const totp = new OTPAuth.TOTP({
      issuer,
      label,
      secret: OTPAuth.Secret.fromBase32(secretBase32),
      digits,
      period,
      algorithm,
    });

    return {
      sourceType: 'otpauth',
      issuer,
      label,
      algorithm,
      digits,
      period,
      secretBase32,
      totp,
    };
  }

  const secretBase32 = normalizeBase32Secret(trimmed);

  if (!secretBase32) {
    throw new Error('Введите корректный Base32 секрет');
  }

  let totp: OTPAuth.TOTP;
  try {
    totp = new OTPAuth.TOTP({
      issuer: '',
      label: '',
      secret: OTPAuth.Secret.fromBase32(secretBase32),
      digits: 6,
      period: 30,
      algorithm: 'SHA1',
    });
  } catch {
    throw new Error('Некорректный Base32 секрет');
  }

  return {
    sourceType: 'base32',
    issuer: '',
    label: '',
    algorithm: 'SHA1',
    digits: 6,
    period: 30,
    secretBase32,
    totp,
  };
}

function stripTrailingSourcePunctuation(value: string): string {
  return value.replace(/[)\]}>,.;'"]+$/, '');
}

interface LocatedTotpSource {
  parsed: ParsedTotpConfig;
  start: number;
  end: number;
}

function parseBase32Candidate(value: string): ParsedTotpConfig | null {
  const normalized = normalizeBase32Secret(value);
  if (normalized.length < 16 || normalized.length > 128 || !/^[A-Z2-7]+$/.test(normalized)) {
    return null;
  }

  try {
    return parseTotpSource(normalized);
  } catch {
    return null;
  }
}

function parseStandaloneBase32(value: string): ParsedTotpConfig | null {
  const trimmed = value.trim();
  if (/^[A-Z2-7-]{16,160}$/i.test(trimmed)) {
    return parseBase32Candidate(trimmed);
  }

  const groups = trimmed.split(/[\s-]+/).filter(Boolean);
  if (groups.length < 2 || groups.some((group) => !/^[A-Z2-7]+$/i.test(group) || ![4, 8].includes(group.length))) {
    return null;
  }
  return parseBase32Candidate(groups.join(''));
}

function findTotpSecretInUrl(source: string): ParsedTotpConfig | null {
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(source);
  } catch {
    return null;
  }

  for (const [key, value] of Array.from(parsedUrl.searchParams.entries())) {
    if (/^(?:secret|2fa|totp|otp)$/i.test(key)) {
      const parsed = parseBase32Candidate(value);
      if (parsed) return parsed;
    }
  }

  const location = `${parsedUrl.hostname}${parsedUrl.pathname}`;
  const hasTotpMarker = /(?:^|[.\-_/])(?:2fa|totp|otp)(?:$|[.\-_/])/i.test(location);
  if (!hasTotpMarker) return null;

  const pathSegments = parsedUrl.pathname.split('/').filter(Boolean).reverse();
  for (const rawSegment of pathSegments) {
    let segment = rawSegment;
    try {
      segment = decodeURIComponent(rawSegment);
    } catch {
      // Повреждённый сегмент URL не должен ломать обработку всей строки.
    }
    const parsed = parseBase32Candidate(segment);
    if (parsed) return parsed;
  }

  return null;
}

function findTotpSourceInLine(line: string): LocatedTotpSource | null {
  const otpAuthPattern = /otpauth:\/\/totp\/[^\s]*/gi;
  let otpAuthMatch: RegExpExecArray | null;

  while ((otpAuthMatch = otpAuthPattern.exec(line)) !== null) {
    const source = stripTrailingSourcePunctuation(otpAuthMatch[0]);
    try {
      return {
        parsed: parseTotpSource(source),
        start: otpAuthMatch.index,
        end: otpAuthMatch.index + source.length,
      };
    } catch {
      // Продолжаем поиск другого поддерживаемого источника в строке.
    }
  }

  const urlPattern = /https?:\/\/[^\s\t]+/gi;
  let urlMatch: RegExpExecArray | null;
  while ((urlMatch = urlPattern.exec(line)) !== null) {
    const source = stripTrailingSourcePunctuation(urlMatch[0]);
    const parsed = findTotpSecretInUrl(source);
    if (parsed) {
      return {
        parsed,
        start: urlMatch.index,
        end: urlMatch.index + source.length,
      };
    }
  }

  const labeledPattern = /(?:^|[\s,;|])(?:secret|2fa|totp|otp)\s*[:=]\s*([A-Z2-7-]{16,160})(?=$|[\s,;|])/gi;
  let labeledMatch: RegExpExecArray | null;
  while ((labeledMatch = labeledPattern.exec(line)) !== null) {
    const source = labeledMatch[1];
    const parsed = parseBase32Candidate(source);
    if (parsed) {
      const start = labeledMatch.index + labeledMatch[0].lastIndexOf(source);
      return { parsed, start, end: start + source.length };
    }
  }

  const standalone = parseStandaloneBase32(line);
  if (standalone) {
    const start = line.search(/\S/);
    const end = line.length - (line.match(/\s*$/)?.[0].length || 0);
    return { parsed: standalone, start, end };
  }

  const candidates: LocatedTotpSource[] = [];
  const fieldPattern = /(^|[\s,;|])([A-Z2-7-]{16,160})(?=$|[\s,;|])/gi;
  let fieldMatch: RegExpExecArray | null;
  while ((fieldMatch = fieldPattern.exec(line)) !== null) {
    const source = fieldMatch[2];
    const parsed = parseBase32Candidate(source);
    if (!parsed) continue;
    const start = fieldMatch.index + fieldMatch[1].length;
    candidates.push({ parsed, start, end: start + source.length });
  }

  const distinctSecrets = new Set(candidates.map((candidate) => candidate.parsed.secretBase32));
  return distinctSecrets.size === 1 ? candidates[0] : null;
}

function replaceTotpSourceInLine(line: string, source: LocatedTotpSource): string {
  const permanentLink = buildPermanentLink(source.parsed.secretBase32);
  return `${line.slice(0, source.start)}${permanentLink}${line.slice(source.end)}`;
}

export function transformBulkInputToPermanentLinks(input: string): BulkLinkTransformResult {
  const newline = input.includes('\r\n') ? '\r\n' : '\n';
  let replaced = 0;
  let unchanged = 0;
  const lines = input.split(/\r?\n/).map((line, index) => {
    if (!line.trim()) return line;

    const source = findTotpSourceInLine(line);
    if (!source) {
      unchanged += 1;
      return line;
    }

    const transformed = replaceTotpSourceInLine(line, source);
    if (transformed !== line) replaced += 1;
    else unchanged += 1;
    return transformed;
  });

  return { text: lines.join(newline), replaced, unchanged };
}

// Клиентский разбор повторяет поддерживаемые сервером форматы для локального режима.
export function parseFlexibleLineClient(line: string, lineNumber: number): LocalTwoFaAccount {
  const trimmed = line.trim();

  if (!trimmed) {
    throw new Error('Пустая строка');
  }

  const source = findTotpSourceInLine(trimmed);
  if (source) {
    const beforeSecret = trimmed.slice(0, source.start).trim();
    const afterSecret = trimmed.slice(source.end).trim();
    const login = beforeSecret.split(/\s+/)[0] || afterSecret.split(/\s+/)[0] || '';
    return {
      localId: generateLocalId(),
      secretBase32: source.parsed.secretBase32,
      issuer: source.parsed.issuer,
      label: source.parsed.label,
      algorithm: source.parsed.algorithm,
      digits: source.parsed.digits,
      period: source.parsed.period,
      displayName: login || buildLocalDisplayName(
        source.parsed.issuer,
        source.parsed.label,
        lineNumber,
      ),
      sourceRaw: trimmed,
    };
  }

  throw new Error('Не удалось распознать otpauth://totp URI или Base32 секрет');
}
